import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useTransform, type PanInfo } from "framer-motion";
import wordmark from "@/assets/backland-wordmark.svg";
import { BookingButton } from "@/components/BookingButton";
import { OffersDialog } from "@/components/OffersDialog";
import { useExitIntentOffers } from "@/hooks/use-exit-intent-offers";
import { logOfferEvent } from "@/lib/offers.functions";
import { useServerFn } from "@tanstack/react-start";
import ExperienceChooser from "@/components/ExperienceChooser";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { CategoryPage } from "@/lib/category-pages.functions";
import type { CategoryRow, SectionRow } from "@/lib/categories.functions";
import { getCategoryComponent } from "@/components/categories";


const DIST_RATIO = 0.18;
const VELOCITY = 450;
const AXIS_LOCK_THRESHOLD = 8;
const SPRING = { type: "tween" as const, duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] };
const MAX_W = 1600;
const NAV_TARGET_KEY = "backland:navTarget";

export type JourneyCategory = {
  id: string;
  label: string;
  short: string;
  kind: "sections" | "page" | "chooser";
  slug: string | null;
  category: CategoryRow | null;
  sections: SectionRow[];
  page?: CategoryPage | null;
};

type Props = {
  categories: JourneyCategory[];
  initialIndex: number;
  selectedPath: string | null;
  chooserError?: string;
  onPickPath: (slug: string) => void;
  /** Index of the last story category — used to append CTA. */
  finalStoryIndex: number;
  /** Preferred target index after picking a path (overrides first-live). */
  postChooserTargetIndex?: number;
  /** Initial tab for the Things To Do category page. */
  initialThingsTab?: "onsite" | "nearby";
};

export default function CategoryJourney({
  categories,
  initialIndex,
  selectedPath,
  chooserError,
  onPickPath,
  finalStoryIndex,
  postChooserTargetIndex,
  initialThingsTab,
}: Props) {
  const selectedPathRef = useRef(selectedPath);
  useEffect(() => {
    selectedPathRef.current = selectedPath;
  }, [selectedPath]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dims, setDims] = useState(() => ({
    w: typeof window !== "undefined" ? Math.min(window.innerWidth, MAX_W) : 390,
    h: typeof window !== "undefined" ? window.innerHeight : 844,
  }));

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setDims({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [c, _setC] = useState(initialIndex);
  const cRef = useRef(initialIndex);
  const setC = useCallback((v: number) => {
    cRef.current = v;
    _setC(v);
  }, []);

  const [activeCatIntent, setActiveCatIntent] = useState(initialIndex);
  useEffect(() => setActiveCatIntent(c), [c]);

  // Virtualize off-screen category slides so a fresh visit to the chooser
  // doesn't eagerly mount every category page (and pull all of its images).
  // First paint mounts ONLY the active slide so nothing else competes for
  // bandwidth. After a short idle delay we expand to active ± 1 so swiping
  // to a neighbor stays instant.
  const [mounted, setMounted] = useState<Set<number>>(() => {
    const s = new Set<number>();
    if (initialIndex >= 0) s.add(initialIndex);
    return s;
  });
  useEffect(() => {
    setMounted((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (let i = c - 1; i <= c + 1; i++) {
        if (i >= 0 && !next.has(i)) {
          next.add(i);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [c]);
  // After first paint, schedule neighbor mount (±2 so the card two-away is
  // already fetching images by the time the user finishes the first swipe).
  useEffect(() => {
    const t = window.setTimeout(() => {
      setMounted((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (let i = cRef.current - 2; i <= cRef.current + 2; i++) {
          if (i >= 0 && !next.has(i)) {
            next.add(i);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 250);
    return () => window.clearTimeout(t);
  }, []);

  // When the user picks a path from the chooser, auto-advance to the first
  // live category for that path. Refs are assigned below once goNextCat/jumpCat exist.
  const goNextCatRef = useRef<() => void>(() => {});
  const jumpCatRef = useRef<(i: number) => void>(() => {});
  const scrollersRef = useRef<(HTMLDivElement | null)[]>([]);
  const pendingTopResetRef = useRef<Map<number, number[]>>(new Map());
  const cancelPendingResets = useCallback((index: number) => {
    const timers = pendingTopResetRef.current.get(index);
    if (timers) {
      timers.forEach((id) => window.clearTimeout(id));
      pendingTopResetRef.current.delete(index);
    }
  }, []);
  const resetCategoryToTop = useCallback((index: number) => {
    cancelPendingResets(index);
    const el = scrollersRef.current[index];
    if (el) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
    // One follow-up reset on the next frame to handle late layout shifts
    // (images/fonts loading). Cancelled immediately if the user scrolls.
    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => {
        const node = scrollersRef.current[index];
        if (node && node.scrollTop < 4) {
          node.scrollTop = 0;
          node.scrollLeft = 0;
        }
        pendingTopResetRef.current.delete(index);
      }, 60),
    );
    pendingTopResetRef.current.set(index, timers);
  }, [cancelPendingResets]);

  useEffect(() => {
    if (!selectedPath) return;
    // Advance whenever a path is picked, even if it's the same one re-selected,
    // as long as the user is currently sitting on the chooser card.
    if (categories[cRef.current]?.kind !== "chooser") return;
    const preferred =
      postChooserTargetIndex != null &&
      postChooserTargetIndex >= 0 &&
      categories[postChooserTargetIndex]?.kind !== "chooser"
        ? postChooserTargetIndex
        : categories.findIndex((cc) => cc.kind !== "chooser");
    if (preferred <= 0) return;
    resetCategoryToTop(preferred);
    const id = window.requestAnimationFrame(() => {
      if (cRef.current === preferred) return;
      if (preferred === cRef.current + 1) goNextCatRef.current();
      else jumpCatRef.current(preferred);
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedPath, categories, resetCategoryToTop, postChooserTargetIndex]);



  const dragX = useMotionValue(0);
  const axisRef = useRef<"x" | "y" | null>(null);
  const animatingRef = useRef(false);
  const suppressClickRef = useRef(false);


  const { w: W, h: H } = dims;
  const outerX = useTransform(dragX, (v) => -c * W + v);

  // Vertical scroll within the active category. Every category switch starts
  // the destination page at the top.
  const [scrollTop, setScrollTop] = useState(0);
  const [thumb, setThumb] = useState({ top: 0, height: 1 }); // fractions 0..1
  const [offersOpen, setOffersOpen] = useState(false);
  const logOffer = useServerFn(logOfferEvent);
  useExitIntentOffers(offersOpen, useCallback(() => {
    setOffersOpen(true);
    logOffer({ data: { eventName: "offer_dialog_auto_open" } }).catch(() => {});
  }, [logOffer]));
  useLayoutEffect(() => {
    const el = scrollersRef.current[c];
    resetCategoryToTop(c);
    if (!el) {
      setScrollTop(0);
      setThumb({ top: 0, height: 1 });
      return;
    }
    const onScroll = () => {
      const sh = el.scrollHeight;
      const ch = el.clientHeight;
      if (el.scrollTop > 0) cancelPendingResets(c);
      setScrollTop(el.scrollTop);
      if (sh <= ch) setThumb({ top: 0, height: 1 });
      else setThumb({ top: el.scrollTop / sh, height: ch / sh });
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [c, categories, resetCategoryToTop, cancelPendingResets]);


  const atBottom = thumb.top + thumb.height >= 0.997;

  const stashNavTarget = useCallback(
    (targetIdx: number) => {
      if (typeof window === "undefined") return;
      if (window.location.pathname.startsWith("/e/")) return;
      const t = categories[targetIdx];
      const targetPath = t?.kind === "page" && t.slug ? `/${t.slug}` : "/";
      if (window.location.pathname !== targetPath) {
        try {
          window.sessionStorage.setItem(NAV_TARGET_KEY, JSON.stringify({ c: targetIdx }));
        } catch {
          /* noop */
        }
      }
    },
    [categories],
  );

  // URL sync intentionally disabled: swiping between cards is one continuous
  // experience and shouldn't trigger a TanStack route remount (which tears
  // down the bottom nav and embedded widgets like Bookeo).


  const goNextCat = useCallback(() => {
    if (animatingRef.current) return;
    const cc = cRef.current;
    if (categories[cc]?.kind === "chooser" && !selectedPathRef.current) {
      animate(dragX, 0, SPRING);
      return;
    }
    if (cc >= categories.length - 1) return;
    resetCategoryToTop(cc + 1);
    stashNavTarget(cc + 1);
    animatingRef.current = true;
    setActiveCatIntent(cc + 1);
    animate(dragX, -W, {
      ...SPRING,
      onComplete: () => {
        setC(cc + 1);
        dragX.set(0);
        animatingRef.current = false;
      },
    });
  }, [W, dragX, categories, setC, stashNavTarget, resetCategoryToTop]);

  const goPrevCat = useCallback(() => {
    if (animatingRef.current) return;
    const cc = cRef.current;
    if (cc <= 0) return;
    resetCategoryToTop(cc - 1);
    stashNavTarget(cc - 1);
    animatingRef.current = true;
    setActiveCatIntent(cc - 1);
    animate(dragX, W, {
      ...SPRING,
      onComplete: () => {
        setC(cc - 1);
        dragX.set(0);
        animatingRef.current = false;
      },
    });
  }, [W, dragX, setC, stashNavTarget, resetCategoryToTop]);

  const jumpCat = useCallback(
    (i: number) => {
      if (i === c || animatingRef.current) return;
      resetCategoryToTop(i);
      stashNavTarget(i);
      setActiveCatIntent(i);
      const distance = Math.abs(i - c);
      if (distance > 1) {
        setC(i);
        dragX.set(0);
        return;
      }
      const dir = i > c ? -1 : 1;
      animatingRef.current = true;
      animate(dragX, dir * W, {
        ...SPRING,
        onComplete: () => {
          setC(i);
          dragX.set(0);
          animatingRef.current = false;
        },
      });
    },
    [c, W, dragX, setC, stashNavTarget, resetCategoryToTop],
  );

  // Wire imperative refs used by the auto-advance effect above.
  goNextCatRef.current = goNextCat;
  jumpCatRef.current = jumpCat;



  // Keyboard nav: ← → between categories. ↑↓ left to native vertical scroll.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNextCat();
      else if (e.key === "ArrowLeft") goPrevCat();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [goNextCat, goPrevCat]);

  const onPanStart = () => {
    axisRef.current = null;
    suppressClickRef.current = false;
  };

  const onPan = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (!axisRef.current) {
      const ax = Math.abs(info.offset.x);
      const ay = Math.abs(info.offset.y);
      if (ax < AXIS_LOCK_THRESHOLD && ay < AXIS_LOCK_THRESHOLD) return;
      axisRef.current = ax > ay ? "x" : "y";
      if (axisRef.current === "x") {
        // Pre-reset both neighbors so the swipe preview reveals them at the top,
        // not at whatever scroll position they were left at previously.
        if (c > 0) {
          const prev = scrollersRef.current[c - 1];
          if (prev) prev.scrollTop = 0;
        }
        if (c < categories.length - 1) {
          const next = scrollersRef.current[c + 1];
          if (next) next.scrollTop = 0;
        }
      }
    }
    if (axisRef.current !== "x") return;
    if (Math.abs(info.offset.x) > 6) suppressClickRef.current = true;
    if (animatingRef.current) return;
    let v = info.offset.x;
    const cat = categories[c];
    const blockForward = cat?.kind === "chooser" && !selectedPathRef.current;
    if ((c === 0 && v > 0) || (c === categories.length - 1 && v < 0) || (blockForward && v < 0)) {
      v *= 0.35;
    }
    dragX.set(v);
  };


  const onPanEnd = (_e: unknown, info: PanInfo) => {
    const axis = axisRef.current;
    axisRef.current = null;
    if (axis !== "x") return;
    const dist = info.offset.x;
    const vel = info.velocity.x;
    const threshold = W * DIST_RATIO;
    if ((dist < -threshold || vel < -VELOCITY) && c < categories.length - 1) goNextCat();
    else if ((dist > threshold || vel > VELOCITY) && c > 0) goPrevCat();
    else if (!animatingRef.current) animate(dragX, 0, SPRING);
    // Reset click suppression on the next tick so the about-to-fire click
    // (from a swipe started on a card) is swallowed but subsequent taps work.
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const onTrackClickCapture = (e: React.MouseEvent) => {
    if (suppressClickRef.current) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const cat = categories[c];

  return (
    <div className="fixed inset-0 overflow-hidden bg-background select-none">
      <div
        ref={containerRef}
        className="relative h-full mx-auto overflow-hidden"
        style={{ maxWidth: MAX_W }}
      >
        {/* Horizontal track of category cards */}
        <motion.div
          className="absolute top-0 left-0 flex"
          style={{ x: outerX, width: categories.length * W, height: H, touchAction: "pan-y" }}
          onPanStart={onPanStart}
          onPan={onPan}
          onPanEnd={onPanEnd}
          onClickCapture={onTrackClickCapture}
        >

          {categories.map((cc, ci) => (
            <div
              key={cc.id}
              className="relative shrink-0 overflow-hidden"
              style={{ width: W, height: H }}
            >
              {mounted.has(ci) ? (
                <CategoryCard
                  category={cc}
                  isActive={ci === c}
                  isLastStory={ci === finalStoryIndex}
                  selectedPath={selectedPath}
                  chooserError={ci === c ? chooserError : undefined}
                  onPickPath={(slug) => {
                    onPickPath(slug);
                    // Always advance to the first live category when a path is
                    // picked from the chooser, even if it's the same path already
                    // selected (selectedPath state won't change, so the effect
                    // above won't fire).
                    if (categories[cRef.current]?.kind !== "chooser") return;
                    const preferred =
                      postChooserTargetIndex != null &&
                      postChooserTargetIndex >= 0 &&
                      categories[postChooserTargetIndex]?.kind !== "chooser"
                        ? postChooserTargetIndex
                        : categories.findIndex((cc) => cc.kind !== "chooser");
                    if (preferred <= 0) return;
                    resetCategoryToTop(preferred);
                    window.requestAnimationFrame(() => {
                      if (cRef.current === preferred) return;
                      if (preferred === cRef.current + 1) goNextCatRef.current();
                      else jumpCatRef.current(preferred);
                    });
                  }}
                  scrollerRef={(el) => {
                    scrollersRef.current[ci] = el;
                  }}
                  initialThingsTab={initialThingsTab}
                />
              ) : null}
            </div>
          ))}
        </motion.div>

        {/* Top bar — hidden on chooser. Transparent until user scrolls, then fades to opaque. */}
        {cat?.kind !== "chooser" && (() => {
          const topOpacity = Math.min(1, scrollTop / 120);
          return (
            <div className="absolute top-0 inset-x-0 z-30 pt-[env(safe-area-inset-top)] pointer-events-none">
              <div
                className="absolute inset-0 transition-opacity duration-200 bg-background/85 backdrop-blur-md"
                style={{ opacity: topOpacity }}
              />
              <div className="relative flex items-center justify-between px-5 pt-4 pb-3">
                <div className="leading-tight">
                  <img src={wordmark} alt="Backland Luxury Nature Resort" className="h-3 w-auto opacity-70" />
                  <div className="font-serif text-base text-foreground/90 italic mt-1">
                    luxury nature resort
                  </div>
                </div>
                <div className="flex items-center gap-2">
                <div className="flex items-center pointer-events-auto rounded-full border border-foreground/25 backdrop-blur-md bg-black/20 overflow-hidden">
                  <div className="relative">
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 rounded-full bg-gold/40 blur-md animate-pulse-glow"
                    />
                    <button
                      type="button"
                      onClick={() => setOffersOpen(true)}
                      className="relative inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-foreground/90 px-3 py-2 hover:bg-black/40 transition"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-gold/90 shadow-[0_0_6px_rgba(212,175,90,0.7)]" />
                      Offers
                    </button>
                  </div>
                  <div className="w-px self-stretch bg-foreground/25" aria-hidden />
                  <BookingButton className="text-[10px] tracking-[0.25em] uppercase text-foreground/80 px-3 py-2 hover:bg-black/40 transition">
                    Reserve
                  </BookingButton>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Right-edge vertical scroll indicator — short thumb showing current position */}
        {cat?.kind !== "chooser" && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 z-30 pointer-events-none h-[34vh] w-[2px] rounded-full bg-foreground/10 overflow-hidden">
            <div
              className="absolute left-0 w-full rounded-full bg-foreground/60"
              style={{
                top: `${thumb.top * 100}%`,
                height: `${Math.max(8, thumb.height * 100)}%`,
              }}
            />
          </div>
        )}

        {/* Swipe-horizontal hint at end of category */}
        {cat?.kind !== "chooser" && (
          <SwipeHorizontalHint show={atBottom && c < categories.length - 1} />
        )}

        {/* Bottom category nav */}
        {selectedPath && (
          <BottomNav
            categories={categories}
            activeIndex={activeCatIntent}
            onJump={jumpCat}
          />
        )}
      </div>
      <OffersDialog open={offersOpen} onOpenChange={setOffersOpen} />
    </div>
  );
}

function CategoryCard({
  category,
  isActive,
  isLastStory,
  selectedPath,
  chooserError,
  onPickPath,
  scrollerRef,
  initialThingsTab,
}: {
  category: JourneyCategory;
  isActive: boolean;
  isLastStory: boolean;
  selectedPath: string | null;
  chooserError?: string;
  onPickPath: (slug: string) => void;
  scrollerRef: (el: HTMLDivElement | null) => void;
  initialThingsTab?: "onsite" | "nearby";
}) {
  const localScrollerRef = useRef<HTMLDivElement | null>(null);
  const setScrollerRef = useCallback(
    (el: HTMLDivElement | null) => {
      localScrollerRef.current = el;
      scrollerRef(el);
    },
    [scrollerRef],
  );

  useLayoutEffect(() => {
    if (!isActive) return;
    const el = localScrollerRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollLeft = 0;
  }, [isActive, category.id]);

  if (category.kind === "chooser") {
    return (
      <ExperienceChooser
        inline
        selectedSlug={selectedPath}
        onPick={onPickPath}
        errorMessage={chooserError}
        isActive={isActive}
      />
    );
  }

  if (category.kind === "page") {

    return (
      <CategoryPagePanel
        page={category.page ?? null}
        label={category.label}
        slug={category.slug ?? null}
        scrollerRef={setScrollerRef}
        isActive={isActive}
      />
    );
  }

  const Component = getCategoryComponent(category.id, category.slug);
  if (!category.category) return null;
  return (
    <div
      ref={setScrollerRef}
      className="absolute inset-0 overflow-y-auto overscroll-contain no-scrollbar"
      style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      aria-hidden={!isActive}
    >
      <Component
        category={category.category}
        sections={category.sections}
        appendCta={isLastStory}
        experienceSlug={selectedPath}
        isActive={isActive}
        initialThingsTab={initialThingsTab}
      />
      <div aria-hidden className="h-[calc(env(safe-area-inset-bottom)+4.33rem)]" />

    </div>
  );
}

function BottomNav({
  categories,
  activeIndex,
  onJump,
}: {
  categories: JourneyCategory[];
  activeIndex: number;
  onJump: (i: number) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const dragStateRef = useRef<{ startX: number; startScroll: number; moved: boolean; pointerId: number } | null>(null);
  const suppressClickRef = useRef(false);
  const [overflow, setOverflow] = useState(false);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const measure = useCallback(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const isOverflow = sc.scrollWidth - sc.clientWidth > 1;
    setOverflow(isOverflow);
    if (!isOverflow) {
      setShowLeft(false);
      setShowRight(false);
      return;
    }
    setShowLeft(sc.scrollLeft > 2);
    setShowRight(sc.scrollLeft + sc.clientWidth < sc.scrollWidth - 2);
  }, []);

  useLayoutEffect(() => {
    measure();
    const sc = scrollerRef.current;
    if (!sc) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(sc);
    return () => ro.disconnect();
  }, [measure]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const el = itemRefs.current[activeIndex];
    if (!scroller || !el) return;
    if (!overflow) {
      measure();
      return;
    }
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const centered = el.offsetLeft - scroller.clientWidth / 2 + el.clientWidth / 2;
    const target = Math.max(0, Math.min(maxScroll, centered));
    scroller.scrollTo({ left: target, behavior: "smooth" });
    const t = window.setTimeout(measure, 350);
    return () => window.clearTimeout(t);
  }, [activeIndex, overflow, measure]);

  const maskImage = overflow
    ? (() => {
        const L = showLeft ? "transparent 0, transparent 8px, black 56px" : "black 0";
        const R = showRight ? "black calc(100% - 56px), transparent calc(100% - 8px), transparent 100%" : "black 100%";
        return `linear-gradient(to right, ${L}, ${R})`;
      })()
    : undefined;

  return (
    <div className="absolute inset-x-0 bottom-0 z-30 pointer-events-none">
      <div className="relative pointer-events-auto bg-background/80 backdrop-blur-md rounded-full px-2 py-1.5">
        <motion.span
          animate={{ opacity: showLeft ? 1 : 0 }}
          transition={{ duration: 0.25 }}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 text-foreground/70 text-[15px] tracking-[0.18em] pointer-events-none select-none"
        >
          …
        </motion.span>
        <motion.span
          animate={{ opacity: showRight ? 1 : 0 }}
          transition={{ duration: 0.25 }}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 text-foreground/70 text-[15px] tracking-[0.18em] pointer-events-none select-none"
        >
          …
        </motion.span>

        <div
          ref={scrollerRef}
          onScroll={overflow ? measure : undefined}
          onPointerDown={(e) => {
            if (!overflow) return;
            if (e.pointerType === "touch") return;
            const sc = scrollerRef.current;
            if (!sc) return;
            dragStateRef.current = {
              startX: e.clientX,
              startScroll: sc.scrollLeft,
              moved: false,
              pointerId: e.pointerId,
            };
          }}
          onPointerMove={(e) => {
            const st = dragStateRef.current;
            if (!st || st.pointerId !== e.pointerId) return;
            const dx = e.clientX - st.startX;
            if (!st.moved && Math.abs(dx) > 4) {
              st.moved = true;
              const sc0 = scrollerRef.current;
              if (sc0) { try { sc0.setPointerCapture(e.pointerId); } catch {} }
            }
            if (!st.moved) return;
            const sc = scrollerRef.current;
            if (sc) sc.scrollLeft = st.startScroll - dx;
          }}
          onPointerUp={(e) => {
            const st = dragStateRef.current;
            if (!st || st.pointerId !== e.pointerId) return;
            const sc = scrollerRef.current;
            if (sc && sc.hasPointerCapture(e.pointerId)) sc.releasePointerCapture(e.pointerId);
            if (st.moved) {
              suppressClickRef.current = true;
              window.setTimeout(() => { suppressClickRef.current = false; }, 0);
            }
            dragStateRef.current = null;
          }}
          onPointerCancel={() => { dragStateRef.current = null; }}
          className={
            overflow
              ? "flex items-center gap-1 overflow-x-auto no-scrollbar scroll-smooth px-4 cursor-grab active:cursor-grabbing"
              : "flex items-center justify-around gap-1 px-6 overflow-hidden"
          }
          style={{
            touchAction: "pan-x",
            ...(maskImage ? { WebkitMaskImage: maskImage, maskImage } : {}),
          }}
        >
          {categories.map((cc, i) => {
            const active = i === activeIndex;
            return (
              <button
                key={cc.id}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                onClick={() => {
                  if (suppressClickRef.current) return;
                  onJump(i);
                }}
                className={`relative shrink-0 px-3 py-2 uppercase transition-all duration-300 ${
                  active
                    ? "text-gold text-[14px] font-semibold tracking-[0.18em] scale-110 opacity-100"
                    : "text-[10px] tracking-[0.22em] font-normal text-foreground/40 hover:text-foreground/70"
                }`}
              >
                {cc.short}
                {active && (
                  <motion.span
                    layoutId="cat-underline"
                    className="absolute left-3 right-3 -bottom-0.5 h-[1.5px] bg-gold/80 rounded-full"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function renderAnswer(text: string) {
  // Parse [label](url) markdown-style links. Other text passes through verbatim
  // so the parent's whitespace-pre-line handles newlines.
  const out: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const url = m[2];
    const external = /^https?:\/\//i.test(url);
    out.push(
      <a
        key={`l-${key++}`}
        href={url}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="text-gold underline underline-offset-2 hover:text-gold/80"
      >
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length === 0 ? text : out;
}

function CategoryPagePanel({
  page,
  label,
  slug,
  scrollerRef,
  isActive,
}: {
  page: CategoryPage | null;
  label: string;
  slug?: string | null;
  scrollerRef: (el: HTMLDivElement | null) => void;
  isActive: boolean;
}) {
  const items = page?.items ?? [];
  const isInfo = slug === "info";
  const isFaq = label.toLowerCase() === "faq" || page?.intro_title?.toLowerCase().includes("frequently");
  const [faqOpen, setFaqOpen] = useState(!isFaq);
  const HeadingTag = isActive ? "h1" : "div";
  return (
    <div
      ref={scrollerRef}
      className="absolute inset-x-0 top-[calc(env(safe-area-inset-top)+5rem)] bottom-[calc(env(safe-area-inset-bottom)+7rem)] overflow-y-auto overscroll-contain no-scrollbar bg-background text-foreground"
      style={{ touchAction: "pan-y pinch-zoom", WebkitOverflowScrolling: "touch", contain: "content" }}
      aria-hidden={!isActive}
    >
      <div className="min-h-full max-w-2xl mx-auto">
        {/* Hero */}
        <header className="px-7 pt-6 pb-5 sm:pt-12 sm:pb-10 border-b border-gold/20">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2 sm:mb-4">{label === "FAQ" ? "Concierge" : label}</div>
          <HeadingTag className="font-serif text-[2rem] sm:text-[2.75rem] md:text-5xl leading-[1.05] tracking-tight">
            {page?.intro_title ?? "Frequently asked"}
          </HeadingTag>
          {page?.intro_body && (
            <p className="mt-3 sm:mt-5 text-sm md:text-base text-foreground/65 font-light leading-relaxed max-w-[52ch]">
              Everything you need to know before your stay at Backland — from dining to pets, amenities, and what to expect.
            </p>
          )}
          {isFaq && items.length > 0 && (
            <button
              onClick={() => setFaqOpen((v) => !v)}
              className="mt-4 sm:mt-7 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-gold hover:text-gold/80 transition"
            >
              <span>{faqOpen ? "HIDE FAQ" : "SHOW FAQ"}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: faqOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.25s ease" }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </header>


        {/* FAQ body */}
        <div className="px-7 pt-4 sm:pt-10">
          <AnimatePresence initial={false}>
            {faqOpen && items.length > 0 && (
              <motion.div
                key="faq-body"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                {(() => {
                  const groups: { name: string | null; items: typeof items }[] = [];
                  for (const it of items) {
                    const name = it.group_name ?? null;
                    const last = groups[groups.length - 1];
                    if (last && last.name === name) last.items.push(it);
                    else groups.push({ name, items: [it] });
                  }
                  return (
                    <div className="space-y-10">
                      {groups.map((g, gi) => (
                        <div key={gi}>
                          {g.name && (
                            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3 pt-2 border-t border-foreground/10">
                              {g.name}
                            </div>
                          )}
                          <Accordion type="single" collapsible className={g.name ? "" : "border-t border-foreground/10"}>
                            {g.items.map((it) => (
                              <AccordionItem key={it.id} value={it.id} className="border-foreground/10">
                                <AccordionTrigger className="font-serif italic text-lg md:text-xl text-foreground py-5 hover:no-underline [&>svg]:text-gold [&>svg]:opacity-90">
                                  {it.question}
                                </AccordionTrigger>
                                <AccordionContent className="text-foreground/65 text-sm md:text-base font-light leading-relaxed pb-6 whitespace-pre-line">
                                  {renderAnswer(it.answer)}
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
          {items.length === 0 && (
            <p className="text-sm text-foreground/55 italic">No questions yet.</p>
          )}
        </div>

        {isInfo && <InfoExtras />}
      </div>
    </div>
  );
}

function InfoExtras() {
  return (
    <div className="mt-6 sm:mt-16">
      {/* Getting Here — raised slab */}
      <section className="px-7 py-8 sm:py-14 bg-[#0c0c0c]">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3 sm:mb-5">Getting Here</div>
        <p className="font-serif text-xl sm:text-2xl md:text-3xl leading-snug text-foreground">
          Tucked into 160 private acres surrounded by National Forest.
        </p>
        <div className="h-px w-12 bg-gold my-4 sm:my-6" />
        <p className="text-sm md:text-base text-foreground/65 font-light leading-relaxed max-w-md">
          Here's the drive in, step by step, with a map of the route.
        </p>
        <a
          href="/getting-here"
          className="inline-block mt-5 sm:mt-7 border border-gold text-gold text-[10px] uppercase tracking-[0.25em] px-7 py-3 sm:py-4 hover:bg-gold hover:text-background transition-colors duration-500"
        >
          View Directions
        </a>
      </section>


      {/* Connect */}
      <section className="px-7 py-16">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-6">Connect</div>
        <ul className="space-y-3">
          <li>
            <a
              href="https://app.dover.com/jobs/backland"
              target="_blank"
              rel="noopener noreferrer"
              className="font-serif italic text-3xl text-foreground hover:text-gold transition-colors"
            >
              Careers
            </a>
          </li>
          <li>
            <span className="font-serif italic text-3xl text-foreground">
              Contact us
            </span>
          </li>
        </ul>

        <div className="mt-10 space-y-2 text-sm font-light">
          <div className="flex gap-4">
            <span className="w-14 shrink-0 text-[10px] uppercase tracking-[0.25em] text-foreground/40 pt-1">Email</span>
            <a href="mailto:info@back.land" className="text-foreground/80 hover:text-gold transition">info@back.land</a>
          </div>
          <div className="flex gap-4">
            <span className="w-14 shrink-0 text-[10px] uppercase tracking-[0.25em] text-foreground/40 pt-1">Phone</span>
            <a href="tel:9282885441" className="text-foreground/80 hover:text-gold transition">928-288-5441</a>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <a
            href="https://www.instagram.com/travelbackland/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="text-foreground/50 hover:text-gold transition"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
          </a>
          <a
            href="https://www.facebook.com/travelbackland/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="text-foreground/50 hover:text-gold transition"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </a>
        </div>

        <p className="mt-8 text-xs text-foreground/45 leading-relaxed">
          6929 E Rosilda Springs Rd.<br />
          Williams AZ 86046
        </p>
      </section>

      {/* Policy footer */}
      <footer className="px-7 pb-16 pt-10 border-t border-foreground/10">
        <div className="flex flex-wrap gap-x-5 gap-y-3">
          <a href="/blog" className="text-[10px] uppercase tracking-[0.25em] text-foreground/40 hover:text-gold transition">Blog</a>
          <a href="/pet-policy" className="text-[10px] uppercase tracking-[0.25em] text-foreground/40 hover:text-gold transition">Pet Policy</a>
          <a href="/privacy" className="text-[10px] uppercase tracking-[0.25em] text-foreground/40 hover:text-gold transition">Privacy</a>
          <a href="/terms" className="text-[10px] uppercase tracking-[0.25em] text-foreground/40 hover:text-gold transition">Terms</a>
        </div>
        <p className="mt-8 text-[9px] uppercase tracking-[0.3em] text-foreground/25">
          © {new Date().getFullYear()} Backland Luxury Nature Resort
        </p>
      </footer>
    </div>
  );
}

function SwipeHorizontalHint({ show }: { show: boolean }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="h-hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="absolute left-1/2 -translate-x-1/2 bottom-[calc(env(safe-area-inset-bottom)+4rem)] z-30 pointer-events-none text-foreground"
        >
          <motion.div
            animate={{ x: [-16, 16, -16] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.4 }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              style={{ transform: "rotate(90deg)", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}
            >
              <path
                d="M10 13V5.5a1.5 1.5 0 0 1 3 0V12"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M13 12v-1a1.2 1.2 0 0 1 2.4 0v2M15.4 13v-.5a1.2 1.2 0 0 1 2.4 0V15M17.8 14.5a1.2 1.2 0 0 1 2.2.6v2.4a4.5 4.5 0 0 1-4.5 4.5h-1.9a4.5 4.5 0 0 1-3.4-1.55l-3.6-4.2a1.3 1.3 0 0 1 1.85-1.8L10 15.5V7"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
